import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSave, FiEdit2, FiRotateCcw, FiX } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import usePagePermissions from '../../hooks/usePagePermissions';
import PageLayout from '../../components/PageLayout';
import {
  ContentCard, FormGrid, FormGroup, Label, Input, Select, SaveButton, Table, Th, Tr, Td,
} from '../../components/SharedStyles';
import { SYSTEM_ACCOUNTS } from '../../config/tranTypes';
import { computeDisposalResult, appreciationOf } from '../../utils/depreciationHelper';
import { findAccountByCode } from '../../utils/journalEntryHelper';
import { loadFixedAssets, postDisposal, updateDisposal, reverseDisposal } from '../../utils/fixedAssetHelper';

const today = () => new Date().toISOString().split('T')[0];

const AssetDisposal = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('fixedAssets', 'disposal');
  const { formatCurrency } = useCurrency();

  const [assets, setAssets] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  // ACC-23: posted disposals can be edited or reversed.
  const [disposed, setDisposed] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    assetId: '',
    disposalType: 'sold',
    disposalDate: today(),
    proceedsMode: 'cash',
    proceeds: '',
    bankId: '',
  });

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const all = await loadFixedAssets(activeCompany.id);
      setAssets(all.filter(a => a.status === 'Active' || a.status === 'FullyDepreciated'));
      setDisposed(all.filter(a => a.status === 'Disposed').sort((a, b) => (b.disposalDate || '').localeCompare(a.disposalDate || '')));
      const bankSnap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'banks'), orderBy('bankCode', 'asc')));
      setBanks(bankSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(b => b.linkedAccountId));
    } catch (e) {
      console.error('Error loading assets:', e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { load(); }, [load]);

  const asset = editing || assets.find(a => a.id === form.assetId) || null;

  const resetForm = () => {
    setEditing(null);
    setForm({ assetId: '', disposalType: 'sold', disposalDate: today(), proceedsMode: 'cash', proceeds: '', bankId: '' });
  };

  const startEdit = (a) => {
    const proceeds = Number(a.disposalProceeds) || 0;
    const bank = a.disposalProceedsAccountId ? banks.find(b => b.linkedAccountId === a.disposalProceedsAccountId) : null;
    setEditing(a);
    setForm({
      assetId: a.id,
      disposalType: a.disposalType || 'sold',
      disposalDate: a.disposalDate || today(),
      proceedsMode: proceeds > 0 ? (bank ? 'bank' : 'cash') : 'none',
      proceeds: proceeds > 0 ? String(proceeds) : '',
      bankId: bank?.id || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReverse = async (a) => {
    if (!canDelete) { alert('You do not have permission to reverse disposals.'); return; }
    if (!window.confirm(`Reverse the disposal of ${a.assetName} (${a.assetCode})${a.disposalDocNumber ? ` — ${a.disposalDocNumber}` : ''}?\n\nIts accounting entry is removed and the asset goes back into the register.`)) return;
    try {
      await reverseDisposal(activeCompany.id, a, currentUser.uid);
      if (editing?.id === a.id) resetForm();
      await load();
      alert(`${a.assetName} is back in service.`);
    } catch (e) {
      alert(`Could not reverse: ${e.message}`);
    }
  };
  const proceedsNum = form.proceedsMode === 'none' ? 0 : (Number(form.proceeds) || 0);
  const result = asset ? computeDisposalResult(asset, proceedsNum) : null;
  const appr = asset ? appreciationOf(asset) : 0; // Asset Appreciation (0 for most assets)

  const resolveProceedsAccount = async () => {
    if (proceedsNum <= 0) return null;
    if (form.proceedsMode === 'bank') {
      const bank = banks.find(b => b.id === form.bankId);
      if (!bank?.linkedAccountId) throw new Error('Select a bank account for the proceeds.');
      const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'accounts', bank.linkedAccountId));
      if (!snap.exists()) throw new Error('Bank account not found in COA.');
      return { id: snap.id, code: snap.data().code, name: snap.data().name };
    }
    // cash
    const cash = await findAccountByCode(activeCompany.id, SYSTEM_ACCOUNTS.CASH_IN_HAND);
    if (!cash?.id) throw new Error('Cash account not found. Run Repair COA.');
    return cash;
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to dispose assets.'); return; }
    if (!asset) { alert('Select an asset.'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year.'); return; }
    if (form.proceedsMode !== 'none' && proceedsNum <= 0) { alert('Enter proceeds, or choose "No proceeds".'); return; }
    if (!window.confirm(editing
      ? `Save changes to the disposal of ${asset.assetName}${asset.disposalDocNumber ? ` (${asset.disposalDocNumber})` : ''}?`
      : `Dispose ${asset.assetName} (${asset.assetCode})? This posts the disposal entry.`)) return;

    setPosting(true);
    try {
      const proceedsAccount = await resolveProceedsAccount();
      if (editing) {
        await updateDisposal(activeCompany.id, {
          asset,
          proceeds: proceedsNum,
          proceedsAccount,
          disposalType: form.disposalType,
          disposalDate: form.disposalDate,
          userId: currentUser.uid,
        });
        alert(`Disposal updated. ${result.isGain ? 'Gain' : 'Loss'}: ${formatCurrency(Math.abs(result.gainLoss))}.`);
        resetForm();
        await load();
        return;
      }
      await postDisposal(activeCompany.id, {
        asset,
        proceeds: proceedsNum,
        proceedsAccount,
        disposalType: form.disposalType,
        disposalDate: form.disposalDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
        branchName: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.branchName : null,
        userId: currentUser.uid,
      });
      alert(`Disposed ${asset.assetName}. ${result.isGain ? 'Gain' : 'Loss'}: ${formatCurrency(Math.abs(result.gainLoss))}.`);
      resetForm();
      await load();
    } catch (err) {
      console.error('Disposal failed:', err);
      alert(`Failed to dispose asset: ${err.message}`);
    } finally {
      setPosting(false);
    }
  };

  return (
    <PageLayout pageTitle="Asset Disposal" breadcrumb="Fixed Assets / Disposal">
      <ContentCard>
        {loading ? (
          <EmptyMsg>Loading assets...</EmptyMsg>
        ) : assets.length === 0 && !editing ? (
          <EmptyMsg>No active assets available for disposal.</EmptyMsg>
        ) : (
          <form onSubmit={handlePost}>
            <FormGrid>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Asset *{editing && <EditTag> Editing disposal {editing.disposalDocNumber || ''} <button type="button" onClick={resetForm}><FiX /> Cancel</button></EditTag>}</Label>
                <Select value={form.assetId} disabled={!!editing} onChange={(e) => setForm({ ...form, assetId: e.target.value })}>
                  {editing && <option value={editing.id}>{editing.assetCode} — {editing.assetName}</option>}
                  <option value="">Select an asset to dispose...</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>{a.assetCode} — {a.assetName} (NBV {formatCurrency(a.netBookValue ?? a.acquisitionCost)})</option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>

            {asset && (
              <NbvCard>
                <NbvItem><span>Acquisition Cost</span><strong>{formatCurrency(asset.acquisitionCost)}</strong></NbvItem>
                {appr > 0 && <NbvItem><span>Appreciation</span><strong>{formatCurrency(appr)}</strong></NbvItem>}
                <NbvItem><span>Accumulated Depreciation</span><strong>{formatCurrency(asset.accumulatedDepreciation || 0)}</strong></NbvItem>
                <NbvItem><span>Net Book Value</span><strong>{formatCurrency(result.nbv)}</strong></NbvItem>
                <NbvItem $highlight $gain={result.isGain}>
                  <span>{result.isGain ? 'Gain on Disposal' : 'Loss on Disposal'}</span>
                  <strong>{formatCurrency(Math.abs(result.gainLoss))}</strong>
                </NbvItem>
              </NbvCard>
            )}

            <FormGrid style={{ marginTop: 16 }}>
              <FormGroup>
                <Label>Disposal Type</Label>
                <Select value={form.disposalType} onChange={(e) => setForm({ ...form, disposalType: e.target.value })}>
                  <option value="sold">Sold</option>
                  <option value="scrapped">Scrapped</option>
                  <option value="written_off">Written Off</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Disposal Date *</Label>
                <Input type="date" value={form.disposalDate} onChange={(e) => setForm({ ...form, disposalDate: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label>Proceeds Received Via</Label>
                <Select value={form.proceedsMode} onChange={(e) => setForm({ ...form, proceedsMode: e.target.value })}>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="none">No proceeds (scrap / write-off)</option>
                </Select>
              </FormGroup>
              {form.proceedsMode !== 'none' && (
                <FormGroup>
                  <Label>Proceeds Amount *</Label>
                  <Input type="number" min="0" step="0.01" value={form.proceeds} onChange={(e) => setForm({ ...form, proceeds: e.target.value })} />
                </FormGroup>
              )}
              {form.proceedsMode === 'bank' && (
                <FormGroup>
                  <Label>Bank Account *</Label>
                  <Select value={form.bankId} onChange={(e) => setForm({ ...form, bankId: e.target.value })}>
                    <option value="">Select bank...</option>
                    {banks.map(b => <option key={b.id} value={b.id}>{b.bankName}{b.branchName ? ` - ${b.branchName}` : ''}</option>)}
                  </Select>
                </FormGroup>
              )}
            </FormGrid>

            {asset && (
              <PostRow>
                <span>
                  Posts: Dr {proceedsNum > 0 ? 'Cash/Bank + ' : ''}Accum. Dep. / Cr Asset Cost{appr > 0 ? ' + Appreciation' : ''}
                  {result.gainLoss > 0 ? ' + Cr Gain' : result.gainLoss < 0 ? ' + Dr Loss' : ''}.
                  {appr > 0 && ' Revaluation surplus moves to Retained Earnings.'}
                </span>
                {canEdit && (
                  <SaveButton $approve type="submit" disabled={posting}>
                    <FiSave /> {posting ? 'Saving...' : (editing ? 'Save Changes' : 'Post Disposal')}
                  </SaveButton>
                )}
              </PostRow>
            )}
          </form>
        )}
      </ContentCard>

      <ContentCard style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 12px', color: colors.text.primary }}>Disposed Assets</h4>
        {disposed.length === 0 ? (
          <EmptyMsg>No disposals yet.</EmptyMsg>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Voucher</Th>
                <Th>Asset</Th>
                <Th>Type</Th>
                <Th>Date</Th>
                <Th style={{ textAlign: 'right' }}>Proceeds</Th>
                <Th style={{ textAlign: 'right' }}>Gain / Loss</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {disposed.map(a => (
                <Tr key={a.id}>
                  <Td><strong>{a.disposalDocNumber || '—'}</strong></Td>
                  <Td>{a.assetCode} — {a.assetName}</Td>
                  <Td>{a.disposalType}</Td>
                  <Td>{a.disposalDate}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(a.disposalProceeds || 0)}</Td>
                  <Td style={{ textAlign: 'right', color: (a.gainLossAmount || 0) >= 0 ? colors.success.main : colors.error.main }}>
                    {(a.gainLossAmount || 0) >= 0 ? 'Gain ' : 'Loss '}{formatCurrency(Math.abs(a.gainLossAmount || 0))}
                  </Td>
                  <Td>
                    {canEdit && a.disposalTransactionId && <SmallBtn type="button" onClick={() => startEdit(a)} title="Edit"><FiEdit2 /></SmallBtn>}
                    {canDelete && <SmallBtn type="button" $danger onClick={() => handleReverse(a)} title="Reverse disposal" style={{ marginLeft: 6 }}><FiRotateCcw /></SmallBtn>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>
    </PageLayout>
  );
};

const EditTag = styled.span`
  margin-left: 10px; font-weight: 500; color: ${colors.primary.main};
  button { margin-left: 8px; background: none; border: 1px solid ${colors.border.light}; border-radius: 6px; padding: 2px 8px; cursor: pointer; color: ${colors.text.secondary}; display: inline-flex; align-items: center; gap: 4px; }
`;
const SmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px; padding: 6px 8px; font-size: 13px;
  background: none; border: 1px solid ${p => p.$danger ? colors.error.main : colors.border.light}; border-radius: 6px;
  color: ${p => p.$danger ? colors.error.main : colors.text.secondary}; cursor: pointer;
`;

const EmptyMsg = styled.div`padding: 40px 20px; text-align: center; color: ${colors.text.secondary};`;
const NbvCard = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-top: 16px;
`;
const NbvItem = styled.div`
  display: flex; flex-direction: column; gap: 4px; padding: 12px 14px; border-radius: 8px;
  background: ${p => p.$highlight ? (p.$gain ? `${colors.success.main}14` : `${colors.error.main}14`) : colors.background.paper};
  border: 1px solid ${p => p.$highlight ? (p.$gain ? colors.success.main : colors.error.main) : colors.border.light};
  span { font-size: 12px; color: ${colors.text.secondary}; }
  strong { font-size: 18px; color: ${p => p.$highlight ? (p.$gain ? colors.success.dark : colors.error.dark) : colors.text.primary}; }
`;
const PostRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 20px;
  padding-top: 16px; border-top: 1px solid ${colors.border.light}; font-size: 13px; color: ${colors.text.secondary};
  flex-wrap: wrap;
`;

export default AssetDisposal;
